// =============================================================================
// Recent-routes trail (frontend → /api/log/frontend-error)
//
// Tiny ring buffer of the last N pathnames the user visited in this session.
// Posted alongside each error event so the Telegram alert reads like:
//
//   Path: /login → /trade → /trade/watchlist → /trade/positions → /trade/order
//   At:   trade/order-abc.js:243 (TradeOrderForm)
//
// Most "what changed?" questions become self-evident from this trail alone.
//
// Implementation notes:
//   • Primary store is a module-level array — fastest, no I/O on the hot
//     navigation path.
//   • Mirror to sessionStorage so a hard reload that immediately throws
//     still carries the trail. Best-effort; sessionStorage can be unavailable
//     (Safari private mode, embedded iframes) so wrapped in try/catch.
//   • Bounded length keeps payload size constant — no unbounded growth.
// =============================================================================

const MAX_ROUTES = 5
const STORAGE_KEY = "__recent_routes"

let buffer: string[] = []
let hydrated = false

function hydrateFromStorage() {
  if (hydrated || typeof window === "undefined") return
  hydrated = true
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        buffer = parsed.filter((s): s is string => typeof s === "string").slice(-MAX_ROUTES)
      }
    }
  } catch {
    // sessionStorage blocked / quota / JSON malformed — proceed with empty buffer.
  }
}

function persist() {
  if (typeof window === "undefined") return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(buffer))
  } catch {
    // Quota or disabled — silently skip; the in-memory copy still serves new errors.
  }
}

export function pushRoute(pathname: string | null | undefined): void {
  if (!pathname || typeof pathname !== "string") return
  hydrateFromStorage()
  // Skip consecutive duplicates so a re-render doesn't pollute the trail.
  if (buffer.length > 0 && buffer[buffer.length - 1] === pathname) return
  buffer.push(pathname)
  if (buffer.length > MAX_ROUTES) buffer = buffer.slice(-MAX_ROUTES)
  persist()
}

export function getRecentRoutes(): string[] {
  hydrateFromStorage()
  return buffer.slice()
}
